"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { readCookieConsent } from "@/components/analytics/browser-attribution";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";
import { COOKIE_CONSENT_NAME } from "@/lib/cookie-consent";
import {
  isCurrentUrlSafeForMicrosoftUet,
  pushMicrosoftUetCommand,
  shouldLoadMicrosoftUet,
} from "@/lib/analytics/microsoft-uet";
import { ANALYTICS_LOCATION_CHANGE_EVENT } from "@/lib/analytics/suppressed-paths";

function subscribeNoop() {
  return () => {};
}

function subscribeToConsent(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === COOKIE_CONSENT_NAME) cb();
  };
  window.addEventListener("cookie_consent_change", cb);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("cookie_consent_change", cb);
    window.removeEventListener("storage", onStorage);
  };
}

function subscribeToLocation(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, cb);
  return () =>
    window.removeEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, cb);
}

function getServerLocationSnapshot(): string {
  return "";
}

function getClientLocationSnapshot(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

function getServerConsentSnapshot(): "accepted" | "declined" | null {
  return null;
}

function getClientConsentSnapshot(): "accepted" | "declined" | null {
  return readCookieConsent();
}

function getServerMicrosoftUetEnabled() {
  return shouldLoadMicrosoftUet();
}

function getClientMicrosoftUetEnabled() {
  if (typeof window === "undefined") return getServerMicrosoftUetEnabled();
  return shouldLoadMicrosoftUet(
    process.env.NEXT_PUBLIC_APP_BASE_URL,
    window.location.hostname,
  );
}

function ensureMicrosoftUetQueue(): MicrosoftUetQueue {
  if (!window.uetq) window.uetq = [];
  return window.uetq;
}

function initialiseMicrosoftUet(tagId: string): boolean {
  if (!window.UET) return false;
  const currentQueue = ensureMicrosoftUetQueue();

  if (Array.isArray(currentQueue)) {
    window.uetq = new window.UET({
      ti: tagId,
      q: currentQueue,
      enableAutoSpaTracking: false,
      enableAutoConsent: false,
    });
  }

  return true;
}

/**
 * Direct Microsoft Advertising UET loader.
 *
 * UET deliberately uses manual SPA page loads. That lets the shared analytics
 * suppression boundary decide whether a URL is safe before Microsoft receives
 * it. The library is not fetched until optional-cookie consent is accepted.
 */
export function MicrosoftUet() {
  const tagId = process.env.NEXT_PUBLIC_MICROSOFT_UET_TAG_ID?.trim() ?? "";
  const envEnabled = useSyncExternalStore(
    subscribeNoop,
    getClientMicrosoftUetEnabled,
    getServerMicrosoftUetEnabled,
  );
  const consent = useSyncExternalStore(
    subscribeToConsent,
    getClientConsentSnapshot,
    getServerConsentSnapshot,
  );
  const pathname = usePathname();
  const locationKey = useSyncExternalStore(
    subscribeToLocation,
    getClientLocationSnapshot,
    getServerLocationSnapshot,
  );
  const suppressed = useAnalyticsSuppressed(pathname);
  const enabled = envEnabled && !suppressed;
  const [libraryReady, setLibraryReady] = useState(false);
  const lastPageKey = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || consent !== "accepted" || !tagId) {
      window.__tsbMicrosoftUetSuppressed = true;
      pushMicrosoftUetCommand("consent", "update", {
        ad_storage: "denied",
      });
      lastPageKey.current = null;
      return;
    }

    window.__tsbMicrosoftUetSuppressed = false;
    const queue = ensureMicrosoftUetQueue();
    if (Array.isArray(queue)) {
      queue.push("consent", "default", { ad_storage: "denied" });
    }
    pushMicrosoftUetCommand("consent", "update", {
      ad_storage: "granted",
    });

    let active = true;
    let script = document.getElementById(
      "microsoft-uet-lib",
    ) as HTMLScriptElement | null;

    function onLoad() {
      if (!script) return;
      script.dataset.loaded = "true";
      if (
        !active ||
        readCookieConsent() !== "accepted" ||
        !isCurrentUrlSafeForMicrosoftUet()
      ) {
        pushMicrosoftUetCommand("consent", "update", {
          ad_storage: "denied",
        });
        return;
      }
      pushMicrosoftUetCommand("consent", "update", {
        ad_storage: "granted",
      });
      setLibraryReady(initialiseMicrosoftUet(tagId));
    }

    if (script?.dataset.loaded === "true") {
      queueMicrotask(() => {
        if (active) setLibraryReady(initialiseMicrosoftUet(tagId));
      });
    } else if (!script) {
      script = document.createElement("script");
      script.id = "microsoft-uet-lib";
      script.async = true;
      script.src = "https://bat.bing.com/bat.js";
      script.addEventListener("load", onLoad, { once: true });
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      active = false;
      script?.removeEventListener("load", onLoad);
      if (script?.dataset.loaded !== "true") script?.remove();
      window.__tsbMicrosoftUetSuppressed = true;
      pushMicrosoftUetCommand("consent", "update", {
        ad_storage: "denied",
      });
      lastPageKey.current = null;
    };
  }, [consent, enabled, tagId]);

  useEffect(() => {
    if (
      !libraryReady ||
      !enabled ||
      consent !== "accepted" ||
      !isCurrentUrlSafeForMicrosoftUet()
    ) {
      return;
    }

    const pageKey = locationKey || `${pathname ?? ""}${window.location.search}`;
    if (lastPageKey.current === pageKey) return;

    pushMicrosoftUetCommand("consent", "update", {
      ad_storage: "granted",
    });
    pushMicrosoftUetCommand("pageLoad");
    lastPageKey.current = pageKey;
  }, [consent, enabled, libraryReady, locationKey, pathname]);

  return null;
}
